import { useEffect, useRef } from 'react';
import type { Mesh, Program, Renderer } from 'ogl';
import { FINE_POINTER_QUERY } from '../../../hooks/useFinePointer';
import { LIGHT_RAYS_FRAGMENT, LIGHT_RAYS_VERTEX } from './lightRaysShader';
import styles from './LightRays.module.css';

interface LightRaysProps {
    speed?: number;
    spread?: number;
    length?: number;
    mouseInfluence?: number;
    className?: string;
}

interface RaysSettings {
    speed: number;
    spread: number;
    length: number;
    mouseInfluence: number;
}

interface RaysScene {
    renderer: Renderer;
    mesh: Mesh;
    program: Program;
}

const MAX_PIXEL_RATIO = 2;
const TOUCH_PIXEL_RATIO = 1;
const ORIGIN_OUTSIDE = 0.2;
const MOUSE_SMOOTHING = 0.92;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

async function createScene(container: HTMLElement, settings: RaysSettings, maxPixelRatio: number): Promise<RaysScene> {
    const { Mesh, Program, Renderer, Triangle } = await import('ogl');
    const renderer = new Renderer({ dpr: Math.min(window.devicePixelRatio, maxPixelRatio), alpha: true });
    const canvas = renderer.gl.canvas as HTMLCanvasElement;
    canvas.className = styles.canvas;
    container.appendChild(canvas);

    const program = new Program(renderer.gl, {
        vertex: LIGHT_RAYS_VERTEX,
        fragment: LIGHT_RAYS_FRAGMENT,
        uniforms: {
            iTime: { value: 0 },
            iResolution: { value: [1, 1] },
            rayPos: { value: [0, 0] },
            rayDir: { value: [0, 1] },
            raysSpeed: { value: settings.speed },
            lightSpread: { value: settings.spread },
            rayLength: { value: settings.length },
            fadeDistance: { value: 1 },
            mousePos: { value: [0.5, 0.5] },
            mouseInfluence: { value: settings.mouseInfluence }
        }
    });

    return { renderer, program, mesh: new Mesh(renderer.gl, { geometry: new Triangle(renderer.gl), program }) };
}

function fitToContainer(scene: RaysScene, container: HTMLElement) {
    scene.renderer.setSize(container.clientWidth, container.clientHeight);
    const width = container.clientWidth * scene.renderer.dpr;
    const height = container.clientHeight * scene.renderer.dpr;
    scene.program.uniforms.iResolution.value = [width, height];
    scene.program.uniforms.rayPos.value = [width / 2, -ORIGIN_OUTSIDE * height];
}

function destroyScene(scene: RaysScene) {
    scene.renderer.gl.getExtension('WEBGL_lose_context')?.loseContext();
    (scene.renderer.gl.canvas as HTMLCanvasElement).remove();
}

export default function LightRays({ speed = 0.8, spread = 0.9, length = 1.6, mouseInfluence = 0.08, className }: LightRaysProps) {
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const isStill = window.matchMedia(REDUCED_MOTION_QUERY).matches;
        const hasFinePointer = window.matchMedia(FINE_POINTER_QUERY).matches;
        const settings = { speed, spread, length, mouseInfluence };
        const pointer = { x: 0.5, y: 0.5 };
        const smoothPointer = { x: 0.5, y: 0.5 };
        let scene: RaysScene | null = null;
        let isVisible = false;
        let frame = 0;

        const render = (time: number) => {
            if (!scene) return;
            smoothPointer.x = smoothPointer.x * MOUSE_SMOOTHING + pointer.x * (1 - MOUSE_SMOOTHING);
            smoothPointer.y = smoothPointer.y * MOUSE_SMOOTHING + pointer.y * (1 - MOUSE_SMOOTHING);
            scene.program.uniforms.iTime.value = time * 0.001;
            scene.program.uniforms.mousePos.value = [smoothPointer.x, smoothPointer.y];
            scene.renderer.render({ scene: scene.mesh });
            if (!isStill) frame = requestAnimationFrame(render);
        };

        const stop = () => {
            cancelAnimationFrame(frame);
            if (scene) destroyScene(scene);
            scene = null;
        };

        const start = async () => {
            if (scene) return;
            const created = await createScene(container, settings, hasFinePointer ? MAX_PIXEL_RATIO : TOUCH_PIXEL_RATIO);
            if (!isVisible || scene) {
                destroyScene(created);
                return;
            }
            scene = created;
            fitToContainer(scene, container);
            frame = requestAnimationFrame(render);
        };

        const trackPointer = (event: PointerEvent) => {
            if (!scene) return;
            const bounds = container.getBoundingClientRect();
            pointer.x = (event.clientX - bounds.left) / bounds.width;
            pointer.y = (event.clientY - bounds.top) / bounds.height;
        };

        const resize = () => scene && fitToContainer(scene, container);
        const visibility = new IntersectionObserver(([entry]) => {
            isVisible = entry.isIntersecting;
            if (isVisible) void start();
            else stop();
        }, { threshold: 0.05 });

        visibility.observe(container);
        window.addEventListener('resize', resize);
        if (hasFinePointer) window.addEventListener('pointermove', trackPointer);

        return () => {
            isVisible = false;
            visibility.disconnect();
            window.removeEventListener('resize', resize);
            window.removeEventListener('pointermove', trackPointer);
            stop();
        };
    }, [speed, spread, length, mouseInfluence]);

    return <div ref={containerRef} className={[styles.rays, className].filter(Boolean).join(' ')} aria-hidden="true" />;
}
